import { useEffect, useMemo, useState } from 'react';
import {
    isPasswordAcceptable,
    PASSWORD_MIN_LENGTH,
    PASSWORD_MIN_SCORE,
    TICKETACK_USER_PASSWORD_MIN_LENGTH,
    TICKETACK_USER_PASSWORD_MIN_SCORE,
    passwordStrengthBarClass,
    passwordStrengthLabelKey,
} from './passwordStrengthCore';

const LANGUAGE_LOADERS = {
    de: () => import('@zxcvbn-ts/language-de'),
    en: () => import('@zxcvbn-ts/language-en'),
    fr: () => import('@zxcvbn-ts/language-fr'),
};

const factoryCache = {};
const inflight = {};
let commonPackagePromise = null;
let corePromise = null;

const resolveLanguage = (language) => {
    const normalized = (language || 'fr').toLowerCase().split('-')[0];
    return LANGUAGE_LOADERS[normalized] ? normalized : 'fr';
};

const loadCommon = () => {
    if (!commonPackagePromise)
        commonPackagePromise = import('@zxcvbn-ts/language-common');
    return commonPackagePromise;
};

const loadCore = () => {
    if (!corePromise)
        corePromise = import('@zxcvbn-ts/core');
    return corePromise;
};

export const preloadPasswordStrength = (language) => {
    const resolved = resolveLanguage(language);
    if (factoryCache[resolved])
        return Promise.resolve(factoryCache[resolved]);
    if (!inflight[resolved]) {
        inflight[resolved] = Promise.all([
            loadCore(),
            loadCommon(),
            LANGUAGE_LOADERS[resolved](),
        ]).then(([core, commonPackage, languagePackage]) => {
            const { ZxcvbnFactory } = core;
            factoryCache[resolved] = new ZxcvbnFactory({
                dictionary: {
                    ...commonPackage.dictionary,
                    ...languagePackage.dictionary,
                },
                graphs: commonPackage.adjacencyGraphs,
                translations: languagePackage.translations,
            });
            return factoryCache[resolved];
        }).catch((err) => {
            delete inflight[resolved];
            console.error('[password] dictionary failed to load', err);
            throw err;
        });
    }
    return inflight[resolved];
};

const pendingStrength = (minScore, minLength) => ({
    result: null,
    acceptable: false,
    score: -1,
    feedback: { warning: '', suggestions: [] },
    minLength,
    minScore,
    ready: false,
    labelKey: passwordStrengthLabelKey(-1),
    barClass: passwordStrengthBarClass(-1),
});

export const evaluatePasswordStrength = (
    password,
    {
        language = 'fr',
        userInputs = [],
        minScore = PASSWORD_MIN_SCORE,
        minLength = PASSWORD_MIN_LENGTH,
    } = {}
) => {
    const factory = factoryCache[resolveLanguage(language)];
    if (!factory)
        return pendingStrength(minScore, minLength);

    const sanitizedInputs = userInputs
        .filter((value) => typeof value === 'string' && value.trim() !== '')
        .map((value) => value.trim());

    const result = password ? factory.check(password, sanitizedInputs) : null;
    const acceptable = isPasswordAcceptable(result, password, sanitizedInputs, minScore, minLength);

    return {
        result,
        acceptable,
        score: result?.score ?? -1,
        feedback: result?.feedback ?? { warning: '', suggestions: [] },
        minLength,
        minScore,
        ready: true,
        labelKey: passwordStrengthLabelKey(result?.score ?? -1),
        barClass: passwordStrengthBarClass(result?.score ?? -1),
    };
};

export function usePasswordDictionary(language = 'fr') {
    const resolved = resolveLanguage(language);
    const [ready, setReady] = useState(() => Boolean(factoryCache[resolved]));

    useEffect(() => {
        let cancelled = false;
        if (factoryCache[resolved]) {
            setReady(true);
            return undefined;
        }
        setReady(false);
        preloadPasswordStrength(resolved).then(() => {
            if (!cancelled)
                setReady(true);
        }).catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [resolved]);

    return ready;
}

export function usePasswordStrength(password, options = {}) {
    const {
        language = 'fr',
        userInputs = [],
        minScore,
        minLength,
    } = options;
    const ready = usePasswordDictionary(language);
    const inputsKey = userInputs.join('\0');

    return useMemo(
        () => evaluatePasswordStrength(password, {
            language,
            userInputs,
            minScore,
            minLength,
        }),
        // userInputs is compared through inputsKey.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [password, language, inputsKey, minScore, minLength, ready]
    );
}

export {
    isPasswordAcceptable,
    PASSWORD_MIN_LENGTH,
    PASSWORD_MIN_SCORE,
    TICKETACK_USER_PASSWORD_MIN_LENGTH,
    TICKETACK_USER_PASSWORD_MIN_SCORE,
    passwordStrengthBarClass,
    passwordStrengthLabelKey,
};
